import { ConfigDatabase } from '../../engine/database';
import { constantsData, type ConstantsConfig, type ConstantKey } from '../../generated/constants';

/**
 * База данных для игровых констант
 * Объединяет кодоген из гугл-таблиц с локальными overrides
 */
export class ConstantsDB extends ConfigDatabase<ConstantsConfig, string> {
  constructor() {
    super({
      base: constantsData,
      overrides: {}, // Пока без локальных правок
      getId: config => config.key,
      validate: (config, id) => {
        if (config.value !== null && typeof config.value === 'number' && config.value < 0) {
          throw new Error(`Constant ${id}: value must be >= 0`);
        }
      },
    });
  }

  /**
   * Получить значение константы по ключу
   */
  getValue(key: ConstantKey): number | undefined {
    const config = this.get(key);
    if (!config || config.value === null) return undefined;
    return typeof config.value === 'number' ? config.value : undefined;
  }

  /**
   * Получить значение константы с fallback
   */
  getOr(key: ConstantKey, fallback: number): number {
    const v = this.getValue(key);
    return v == null ? fallback : v;
  }

  /**
   * Проверить наличие ключа
   */
  hasKey(key: ConstantKey): boolean {
    return this.has(key);
  }

  /**
   * Получить все доступные ключи
   */
  keys(): ConstantKey[] {
    return this.ids() as ConstantKey[];
  }
}

// Ленивый синглтон, чтобы избежать проблем с порядком инициализации
let _constantsInstance: ConstantsDB | null = null;
export function getConstants(): ConstantsDB {
  if (!_constantsInstance) {
    _constantsInstance = new ConstantsDB();
  }
  return _constantsInstance;
}

// Backward compatibility - используем Proxy для ленивого доступа
export const Constants = new Proxy({} as ConstantsDB, {
  get(_target, prop) {
    const instance = getConstants();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const method = instance[prop];

    if (typeof method === 'function') {
      return method.bind(instance);
    }
    return method;
  },
});

// Реэкспортируем типы для удобства
export type { ConstantKey };
